import { logCustomEvent as brazeCustomEvent } from "@braze/web-sdk";
import { checkIsAgent, checkIsPerformer, checkIsCD } from "shared-auth";
import {
  BrazeCustomEvents,
  HeapCustomEvent,
  HeapService,
} from "shared-services";
import { MediaAssetType } from "mediaModules/media/models";

const PATH_NAME = window.location.pathname;
const VIDEO_ENDPOINT = "/profile/media/video";
const AUDIO_ENDPOINT = "/profile/media/audio";

const profileUpdate = () => {
  HeapService.track(HeapCustomEvent.ProfileRedesignUpdate);

  try {
    brazeCustomEvent(BrazeCustomEvents.PerformerProfileUpdated);
  } catch (error) {
    // do nothing (e.g. braze not initialized / anonymous user)
  }
};

const audioClipsPerformerUpdate = () => {
  HeapService.track(HeapCustomEvent.ProfileRedesignAudioClipsPerformer);
};

const audioClipsAgentUpdate = () => {
  HeapService.track(HeapCustomEvent.ProfileRedesignAudioClipsAgent);
};

const videoPerformerUpdate = () => {
  HeapService.track(HeapCustomEvent.ProfileRedesignVideosPerformer);
};

const videoAgentUpdate = () => {
  HeapService.track(HeapCustomEvent.ProfileRedesignVideosAgent);
};

const cdAudioVideoPlayed = () => {
  HeapService.track(HeapCustomEvent.ProfileRedesignCDAudioVideoPlayed);
};

const agentAudioVideoPlayed = () => {
  HeapService.track(HeapCustomEvent.ProfileRedesignAgentAudioVideoPlayed);
};

const profileEdited = () => {
  HeapService.track(HeapCustomEvent.ProfileRedesignIsEdited);
};

const checkIsPerformerWithAudioEndpoint = () =>
  checkIsPerformer() && PATH_NAME === AUDIO_ENDPOINT;

const checkIsAgentWithAudioEndpoint = () =>
  checkIsAgent() && PATH_NAME === AUDIO_ENDPOINT;

const checkIsPerformerWithVideoEndpoint = () =>
  checkIsPerformer() && PATH_NAME === VIDEO_ENDPOINT;

const checkIsAgentWithVideoEndpoint = () =>
  checkIsAgent() && PATH_NAME === VIDEO_ENDPOINT;

export const heapTrackingForChangeMainPhotoFlow = () => {
  if (checkIsPerformer()) {
    HeapService.track(HeapCustomEvent.ProfileRedesignHeadShotPerformer);
  }

  if (checkIsAgent()) {
    HeapService.track(HeapCustomEvent.ProfileRedesignHeadShotAgent);
  }

  profileUpdate();
};

export const heapTrackingForControlsContainer = () => {
  if (checkIsPerformerWithAudioEndpoint()) {
    audioClipsPerformerUpdate();
  }

  if (checkIsAgentWithAudioEndpoint()) {
    audioClipsAgentUpdate();
  }

  if (checkIsPerformerWithVideoEndpoint()) {
    videoPerformerUpdate();
  }

  if (checkIsAgentWithVideoEndpoint()) {
    videoAgentUpdate();
  }
};

export const trackMediaFileEdit = (mediaType: MediaAssetType) => {
  const isVideo = mediaType === MediaAssetType.Video;
  const mediaUpdatePerformerFn = isVideo
    ? videoPerformerUpdate
    : audioClipsPerformerUpdate;
  const mediaUpdateAgentFn = isVideo ? videoAgentUpdate : audioClipsAgentUpdate;

  if (checkIsPerformer()) {
    mediaUpdatePerformerFn();
  }

  if (checkIsAgent()) {
    mediaUpdateAgentFn();
  }

  profileUpdate();
};

export const heapTrackingForAddNewAudiPage = () => {
  if (checkIsPerformer()) {
    audioClipsPerformerUpdate();
  }

  if (checkIsAgent()) {
    audioClipsAgentUpdate();
  }

  profileUpdate();
};

export const heapTrackingForReorderableList = () => {
  heapTrackingForControlsContainer();
};

export const heapTrackingForAddNewVideoPage = () => {
  if (checkIsPerformer()) {
    videoPerformerUpdate();
  }

  if (checkIsAgent()) {
    videoAgentUpdate();
  }

  profileUpdate();
};

export const heapTrackingForBasicInfoPage = () => {
  if (checkIsPerformer()) {
    HeapService.track(HeapCustomEvent.ProfileRedesignDetailsPerformer);
  }

  if (checkIsAgent()) {
    HeapService.track(HeapCustomEvent.ProfileRedesignDetailsAgent);
  }

  profileUpdate();
};

export const heapTrackingForSkillsPageSkillUpdate = () => {
  if (checkIsPerformer()) {
    HeapService.track(HeapCustomEvent.ProfileRedesignSkillsPerformer);
  }

  if (checkIsAgent()) {
    HeapService.track(HeapCustomEvent.ProfileRedesignSkillsAgent);
  }
};

export const heapTrackingForAboutMePage = () => {
  if (checkIsPerformer()) {
    HeapService.track(HeapCustomEvent.ProfileRedesignHighlightsPerformer);
  }

  if (checkIsAgent()) {
    HeapService.track(HeapCustomEvent.ProfileRedesignHighlightsAgent);
  }

  profileUpdate();
};

export const heapTrackingForEditCreditPage = () => {
  if (checkIsPerformer()) {
    HeapService.track(HeapCustomEvent.ProfileRedesignCreditsPerformer);
  }

  if (checkIsAgent()) {
    HeapService.track(HeapCustomEvent.ProfileRedesignCreditsAgent);
  }

  profileUpdate();
};

export const heapTrackingForAddAgencyPage = () => {
  profileUpdate();
};

export const heapTrackingForAgenciesPage = () => {
  profileUpdate();
};

export const heapTrackingForSkillsPageProfileUpdate = () => {
  profileUpdate();
};

export const heapTrackingForTrainingPage = () => {
  profileUpdate();
};

export const heapTrackingForMediaSection = () => {
  if (checkIsCD()) {
    cdAudioVideoPlayed();
  }

  if (checkIsAgent()) {
    agentAudioVideoPlayed();
  }
};

export const heapTrackingForAudioClips = () => {
  if (checkIsAgent()) {
    agentAudioVideoPlayed();
  }

  if (checkIsCD()) {
    cdAudioVideoPlayed();
  }
};

export const heapTrackingForShowreels = () => {
  heapTrackingForAudioClips();
};

export const heapTrackingForMainPageEmailProfile = () => {
  if (checkIsAgent()) {
    HeapService.track(HeapCustomEvent.ProfileRedesignAgentEmailProfile);
  }

  if (checkIsCD()) {
    HeapService.track(HeapCustomEvent.ProfileRedesignCDEmailProfile);
  }
};

export const heapTrackingForMainPagePrintProfile = () => {
  if (checkIsAgent()) {
    HeapService.track(HeapCustomEvent.ProfileRedesignAgentPrintProfile);
  }

  if (checkIsCD()) {
    HeapService.track(HeapCustomEvent.ProfileRedesignCDPrintProfile);
  }
};

export function heapTrackingProfileEdit() {
  if (checkIsPerformer() || checkIsAgent()) {
    profileEdited();
  }
}

export const heapTrackingForAgenciesSection = () => {
  if (checkIsPerformer() || checkIsAgent()) {
    profileEdited();
  }
};

export const heapTrackingForBasicInfoSection = () => {
  heapTrackingForAgenciesSection();
};

export const heapTrackingForEditPanel = () => {
  heapTrackingForAgenciesSection();
};

export const heapTrackingForRowGallery = () => {
  heapTrackingForAgenciesSection();
};

export const heapTrackingForReorderableMediaAssets = () => {
  if (checkIsPerformerWithAudioEndpoint()) {
    audioClipsPerformerUpdate();
  }
};

export const heapTrackingForShowreelsSortCompleted = () => {
  if (checkIsPerformerWithVideoEndpoint()) {
    videoPerformerUpdate();
  }
};
